import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Globe, ArrowRight, MapPin, Shield, Zap, Users, AlertCircle } from 'lucide-react';
import { fetchXMRStats, formatCurrency, formatLargeNumber, XMRStats } from '../services/cryptoApi';
import { fetchAllXMRBusinesses } from '../services/overpassApi';
import { Business } from '../types';

interface GlobeSplashProps {
  onEnter: (businesses: Business[]) => void;
}

interface RealBusinessLocation {
  lat: number;
  lng: number;
  category: string;
  name: string;
  description: string;
  city: string;
  country: string;
  verified: boolean;
  business: Business;
}

const categoryColors = {
  restaurant: '#10b981',
  tech: '#3b82f6',
  retail: '#8b5cf6',
  service: '#eab308',
  hotel: '#ec4899',
  entertainment: '#ef4444',
  default: '#6b7280',
};

const features = [
  { icon: Shield, title: 'Privacy First', description: 'Complete anonymity with Monero payments' },
  { icon: Globe, title: 'Global Network', description: 'Real businesses worldwide accepting XMR' },
  { icon: Zap, title: 'Real-time Data', description: 'Live updates from OpenStreetMap' },
  { icon: Users, title: 'Community Driven', description: 'Built by the privacy community' }
];

const loadingSteps = [
  "Initializing...",
  "Loading business network & stats...",
  "Processing global locations...",
  "Rendering 3D globe...",
  "Finalizing user interface...",
  "Welcome!"
];

// Extracted content component to avoid code duplication and for better readability
const SplashContent: React.FC<{
    onEnter: () => void;
    allStats: any;
    statsError: string | null;
}> = ({ onEnter, allStats, statsError }) => (
    <>
        <div className="animate-fade-in-up">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-white via-orange-200 to-orange-400 bg-clip-text text-transparent mb-2 md:mb-4">Explore the Monero Economy</h1>
            <p className="text-sm sm:text-base lg:text-xl text-gray-300 max-w-xl leading-relaxed mx-auto">Discover businesses worldwide that accept Monero payments. Privacy-focused commerce mapping for the decentralized economy.</p>
        </div>
        
        <div className="animate-fade-in-up animation-delay-300 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-white bg-black/30 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-gray-700/50">
            {statsError ? (
                <div className="col-span-full flex items-center justify-center gap-2 text-red-400 text-sm"><AlertCircle size={16} /> {statsError}</div>
            ) : allStats ? (
                <>
                    <div className="text-center"><p className="text-base sm:text-lg md:text-2xl font-bold text-orange-400">{allStats.price}</p><p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider">XMR Price</p></div>
                    <div className="text-center"><p className="text-base sm:text-lg md:text-2xl font-bold">{allStats.businesses}</p><p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider">Businesses</p></div>
                    <div className="text-center"><p className="text-base sm:text-lg md:text-2xl font-bold">{allStats.volume}</p><p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider">24h Volume</p></div>
                    <div className="text-center"><p className="text-base sm:text-lg md:text-2xl font-bold">{allStats.countries}</p><p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider">Countries</p></div>
                </>
            ) : <div className="col-span-4 text-center text-gray-400 text-sm">Loading stats...</div>}
        </div>

        <div className="animate-fade-in-up animation-delay-600 grid grid-cols-2 gap-2 sm:gap-4">
            {features.map(feature => (
                <div key={feature.title} className="bg-black/30 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-gray-700/50">
                    <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400 mb-1 sm:mb-2"/>
                    <h3 className="font-semibold text-sm sm:text-base text-white">{feature.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-400">{feature.description}</p>
                </div>
            ))}
        </div>

        <div className="animate-fade-in-up animation-delay-900 pointer-events-auto">
          <button onClick={onEnter} className="group w-full lg:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 sm:px-8 sm:py-4 rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/25 flex items-center justify-center gap-2 sm:gap-3">
            <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Explore the Map</span>
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
    </>
);


export const GlobeSplash: React.FC<GlobeSplashProps> = ({ onEnter }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const isInitializedRef = useRef(false);
  
  const [loading, setLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [xmrStats, setXMRStats] = useState<XMRStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [liveBusinesses, setLiveBusinesses] = useState<RealBusinessLocation[]>([]);
  const [businessesLoaded, setBusinessesLoaded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Effect to load all initial data in parallel for better performance
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setCurrentStepIndex(1);

        // Fetch businesses and stats concurrently
        const [businesses, stats] = await Promise.all([
          fetchAllXMRBusinesses(),
          fetchXMRStats()
        ]);

        // Process and set business data
        const businessLocations: RealBusinessLocation[] = businesses.map((business) => {
            const addressParts = business.address ? business.address.split(',').map(part => part.trim()) : [];
            const country = addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown';
            const city = addressParts.length > 1 ? addressParts[addressParts.length - 2] : 'Unknown';
            return {
                lat: business.coordinates.lat,
                lng: business.coordinates.lng,
                category: business.category,
                name: business.name,
                description: business.description || `${business.category} accepting XMR payments`,
                city,
                country,
                verified: !!business.osmId,
                business
            };
        });
        setLiveBusinesses(businessLocations);
        
        // Update stats with the correct business count
        if (stats) {
            setXMRStats({ ...stats, businessCount: businessLocations.length });
        } else {
            setStatsError("Stats unavailable");
        }

      } catch (error) {
        console.error("Failed to load initial data:", error);
        setStatsError("Stats unavailable");
      } finally {
        // This ensures the globe rendering can proceed even if data fetching fails
        setBusinessesLoaded(true);
      }
    };

    loadInitialData();
  }, []);

  // Effect to handle the exit animation and transition
  useEffect(() => {
    if (isExiting) {
      // Wait for the fade-out animation (500ms) to complete before calling onEnter
      const timer = setTimeout(() => {
        onEnter(liveBusinesses.map(loc => loc.business));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isExiting, onEnter, liveBusinesses]);


  useEffect(() => {
    if (!mountRef.current || !businessesLoaded || isInitializedRef.current) return;

    let animationId: number;
    let controls: OrbitControls;
    
    const latLngToVector3 = (lat: number, lng: number, radius: number) => {
        const phi = (90 - lat) * (Math.PI / 180);
        const theta = (lng + 180) * (Math.PI / 180);
        return new THREE.Vector3(
            -radius * Math.sin(phi) * Math.cos(theta),
            radius * Math.cos(phi),
            radius * Math.sin(phi) * Math.sin(theta)
        );
    };

    const createMarkers = () => {
        const group = new THREE.Group();
        liveBusinesses.forEach((location, index) => {
            const markerGroup = new THREE.Group();
            const position = latLngToVector3(location.lat, location.lng, 2.05);
            
            const markerSphere = new THREE.Mesh(
                new THREE.SphereGeometry(window.innerWidth < 1024 ? 0.03 : 0.025, 16, 16),
                new THREE.MeshPhongMaterial({
                    color: location.verified ? 0xFF6600 : 0xFFAA33,
                    emissive: new THREE.Color(0xFF3300),
                    emissiveIntensity: 0.5,
                    shininess: 100
                })
            );

            const ringColor = categoryColors[location.category as keyof typeof categoryColors] || categoryColors.default;
            const ring = new THREE.Mesh(
                new THREE.RingGeometry(0.04, 0.06, 32),
                new THREE.MeshBasicMaterial({
                    color: new THREE.Color(ringColor),
                    transparent: true,
                    opacity: 0.7,
                    side: THREE.DoubleSide
                })
            );
            ring.lookAt(0,0,0);

            markerGroup.add(markerSphere);
            markerGroup.add(ring);
            markerGroup.position.copy(position);
            markerGroup.userData = { isMarker: true, index };
            group.add(markerGroup);
        });
        return group;
    };


    const initScene = async () => {
      isInitializedRef.current = true;
      setCurrentStepIndex(3);

      const scene = new THREE.Scene();
      const clock = new THREE.Clock();
      const camera = new THREE.PerspectiveCamera(20, window.innerWidth / window.innerHeight, .1, 1000);
      
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      mountRef.current?.appendChild(renderer.domElement);
      
      const globeGroup = new THREE.Group();
      scene.add(globeGroup);
      
      const cameraDistance = 15;
      camera.position.z = cameraDistance;
      
      // Always center the globe now
      globeGroup.position.x = 0;
      camera.position.x = 0;

      controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.enableZoom = false; 
      controls.enablePan = false;
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.4;
      
      controls.target.copy(globeGroup.position);

      setCurrentStepIndex(4);
      const textureLoader = new THREE.TextureLoader();
      const textureUrls = [
          { url: 'https://jeromeetienne.github.io/threex.planets/images/earthbump1k.jpg', name: 'topo' },
          { url: 'https://jeromeetienne.github.io/threex.planets/images/earthspec1k.jpg', name: 'specular' },
          { url: 'https://jeromeetienne.github.io/threex.planets/images/earthcloudmap.jpg', name: 'clouds_base' },
          { url: 'https://jeromeetienne.github.io/threex.planets/images/earthcloudmaptrans.jpg', name: 'cloud_alpha' },
      ];
      const loadedTextures: { [key: string]: THREE.Texture } = {};
      await Promise.all(textureUrls.map(({ url, name }) => new Promise<void>((resolve) => {
        textureLoader.load(url, texture => { loadedTextures[name] = texture; resolve(); }, undefined, () => resolve());
      })));
      
      setCurrentStepIndex(5);

      const globeMaterial = new THREE.MeshPhongMaterial({
          map: loadedTextures.topo,
          color: new THREE.Color(0x000000),
          emissive: new THREE.Color(0xFF6600),
          emissiveMap: loadedTextures.topo,
          emissiveIntensity: 5,
          specularMap: loadedTextures.specular,
          specular: new THREE.Color('grey'),
          shininess: 50,
          displacementMap: loadedTextures.topo,
          displacementScale: 0.10,
          displacementBias: -0.04,
      });
      
      const globe = new THREE.Mesh(new THREE.SphereGeometry(2, 128, 128), globeMaterial);
      globeGroup.add(globe);

      const clouds = new THREE.Mesh(
        new THREE.SphereGeometry(2.06, 64, 64),
        new THREE.MeshPhongMaterial({
          map: loadedTextures.clouds_base,
          alphaMap: loadedTextures.cloud_alpha,
          transparent: true,
          opacity: 0.2,
          depthWrite: false,
        })
      );
      globeGroup.add(clouds);

      const markers = createMarkers();
      globeGroup.add(markers);
      
      const ambientLight = new THREE.AmbientLight(0xFF6600, .5);
      scene.add(ambientLight);
      const directionalLight = new THREE.DirectionalLight(0xffffff, 2);
      directionalLight.position.set(-5, 3, 5);
      scene.add(directionalLight);

      const handleResize = () => {
          camera.aspect = window.innerWidth / window.innerHeight;
          camera.updateProjectionMatrix();
          renderer.setSize(window.innerWidth, window.innerHeight);
          
          // Globe is always centered, no need for resize logic here
          globeGroup.position.x = 0;
          camera.position.x = 0;
          controls.target.copy(globeGroup.position);
      };
      window.addEventListener('resize', handleResize);

      const animate = () => {
          animationId = requestAnimationFrame(animate);
          const elapsedTime = clock.getElapsedTime();
          
          markers.children.forEach((markerGroup) => {
              if (markerGroup.userData.isMarker) {
                  const { index } = markerGroup.userData;
                  const pulse = 1.0 + 0.2 * Math.sin(elapsedTime * 5 + index * 0.3);
                  markerGroup.scale.set(pulse, pulse, pulse);

                  const ring = markerGroup.children[1] as THREE.Mesh;
                  if (ring) {
                      ring.rotation.z -= 0.03;
                  }
              }
          });

          controls.update();
          renderer.render(scene, camera);
      };
      animate();
      
      setCurrentStepIndex(6);
      setTimeout(() => {
          setLoading(false);
          setShowContent(true);
      }, 500);

      return () => {
          window.removeEventListener('resize', handleResize);
          cancelAnimationFrame(animationId);
          controls.dispose();
          if(mountRef.current && renderer.domElement) {
              mountRef.current.removeChild(renderer.domElement);
          }
      };
    };

    initScene();

    return () => {
      if (controls) controls.dispose();
      if (animationId) cancelAnimationFrame(animationId);
    }
  }, [businessesLoaded, liveBusinesses]);

  const allStats = xmrStats ? {
      price: formatCurrency(xmrStats.price.current_price),
      businesses: `${liveBusinesses.length}+`,
      volume: formatLargeNumber(xmrStats.price.volume_24h),
      countries: `${xmrStats.countriesCount}`
  } : null;

  const handleEnter = () => {
    setIsExiting(true);
  };

  return (
    <div className={`relative w-full h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 overflow-hidden transition-opacity duration-500 ${isExiting ? 'opacity-0' : 'opacity-100'}`}>
      <div ref={mountRef} className="absolute inset-0 z-0 cursor-grab active:cursor-grabbing" />
      
      {/* --- LAYOUT CONTAINER --- */}
      <div className="absolute inset-0 z-20 flex items-center justify-center p-4 sm:p-6 lg:p-12 pointer-events-none">
        <div className="w-full max-w-2xl space-y-4 sm:space-y-6 text-center">
            {showContent && <SplashContent onEnter={handleEnter} allStats={allStats} statsError={statsError} />}
        </div>
      </div>


      {loading && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800 flex items-center justify-center z-50">
          <div className="text-center px-4 max-w-2xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-white">{loadingSteps[currentStepIndex]}</h2>
          </div>
        </div>
      )}

      <style jsx>{`
        .animate-fade-in-up { animation: fadeInUp 0.8s ease-out forwards; opacity: 0; }
        .animation-delay-300 { animation-delay: 0.3s; }
        .animation-delay-600 { animation-delay: 0.6s; }
        .animation-delay-900 { animation-delay: 0.9s; }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
